import React, { useEffect, useMemo, useState } from "react";
import { SensorDataFileRow } from "@shared/models";
import { SensorDataSessionProcessedValues } from "@containers/SensorDataSession/interfaces";
import { useDebounce } from "@shared/hooks";

import "./index.scss";

interface CustomTooltipProps {
  payload?: any[];
  label?: string;
  refAreaLeft: SensorDataFileRow | null;
  refAreaRight: SensorDataFileRow | null;
  processedValues?: SensorDataSessionProcessedValues[];
  onChangeActiveDot?: (payload: { seconds: number; station_code: string }) => void;
}

const CustomTooltip = ({
  payload,
  label,
  refAreaLeft,
  refAreaRight,
  processedValues,
  onChangeActiveDot,
}: CustomTooltipProps) => {
  const [activeDotValue, setActiveDotValue] = useState<string | null>(null);

  const debounceValue = useDebounce<string | null>(activeDotValue, 500);

  const timeRangeLabel = useMemo(() => {
    if (!refAreaLeft || !refAreaRight) return "";
    const [left, right] = [refAreaLeft.time, refAreaRight.time].sort();
    return `${left} - ${right}`;
  }, [refAreaLeft, refAreaRight]);

  const timeDiffLabel = useMemo(() => {
    if (!refAreaLeft || !refAreaRight) return "";
    const [left, right] = [refAreaLeft.seconds, refAreaRight.seconds].sort();
    return (right - left).toFixed(4);
  }, [refAreaLeft, refAreaRight]);

  const labelTimeText = useMemo(() => {
    return label;
  }, [label]);

  const firstPayload = useMemo(() => {
    return payload?.[0];
  }, [payload]);

  useEffect(() => {
    if (!firstPayload?.payload?.original_seconds || !firstPayload?.payload?.station_code) {
      setActiveDotValue(null);
      return;
    }

    setActiveDotValue(`${firstPayload?.payload?.original_seconds}|${firstPayload?.payload?.station_code}`);
  }, [firstPayload?.payload?.original_seconds, firstPayload?.payload?.station_code, onChangeActiveDot]);

  useEffect(() => {
    if (debounceValue) {
      const [seconds, code] = debounceValue.split("|");
      onChangeActiveDot?.({
        seconds: Number(seconds),
        station_code: code,
      });
    }
  }, [debounceValue, onChangeActiveDot]);

  return payload?.length ? (
    <div className="custom-tooltip">
      <div className="label-text">Time: {labelTimeText}</div>
      <div className="label-text">
        {payload?.map((p) => (
          <div key={p.dataKey} className="label-text-item" style={{ color: p.color }}>
            {p.payload.original_time !== undefined && <div>MongoDB Time: {p.payload.original_time}</div>}
            <div>Value: {p.payload[p.dataKey]}</div>
            {p.payload.raw_value !== undefined && <div>Raw Value: {p.payload.raw_value}</div>}
            {p.payload.tuning !== undefined && <div>Tuning: {p.payload.tuning}</div>}
            {p.payload.tuning_reset !== undefined && <div>Tuning Reset: {p.payload.tuning_reset}</div>}
            {processedValues?.map((pv) => (
              <div key={pv.id}>
                {pv.parameter}: {pv.time} - Value: {pv.value}, Artifact: {pv.artifact}
              </div>
            ))}
          </div>
        ))}
      </div>
      <div className="custom-content">
        {refAreaLeft && refAreaRight ? <div className="selected-items">Time Range: {timeRangeLabel}</div> : null}
      </div>
      <div className="custom-content">
        {refAreaLeft && refAreaRight ? <div className="selected-items">Time Difference: {timeDiffLabel}s</div> : null}
      </div>
    </div>
  ) : null;
};

export default CustomTooltip;
